import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap, useMapEvents, Marker, Polygon, LayerGroup, LayersControl } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@/components/ui/button";
import { PenLine, X, Eye, EyeOff, Trash2, Pencil, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";

const fmtEuro = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const fmtPct = (n) => (n === null || n === undefined) ? "—" : `${(n * 100).toFixed(1)}%`;

const makeDot = (color) =>
  L.divIcon({
    className: "custom-dot",
    html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.35);"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -8],
  });

const PopupLinkWithPreview = ({ href, preview }) => {
  const [hover, setHover] = React.useState(false);
  const [imgOk, setImgOk] = React.useState(true);
  return (
    <div className="relative inline-block mt-1" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <a href={href} target="_blank" rel="noreferrer" className="text-[#007AFF] text-[12px] inline-block">Apri annuncio →</a>
      {hover && preview && imgOk && (
        <div className="pointer-events-none" style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 10000, width: 220, height: 140, borderRadius: 10, overflow: "hidden", border: "1px solid #e5e7eb", background: "#fff", boxShadow: "0 10px 30px rgba(0,0,0,0.25)" }}>
          <img src={preview} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={() => setImgOk(false)} />
        </div>
      )}
    </div>
  );
};

const AutoZoom = ({ pointsKey, points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) { map.setView(points[0], 13); return; }
    map.fitBounds(points, { padding: [40, 40] });
    // NOTE: depending on `pointsKey` (a stringified representation of coords)
    // instead of `points` prevents the map from re-fitting on every re-render
    // — e.g. when the user starts drawing or enters edit mode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointsKey, map]);
  return null;
};

const FocusHandler = ({ focused, markerRefs }) => {
  const map = useMap();
  useEffect(() => {
    if (!focused || !focused.lat || !focused.lon) return;
    map.flyTo([focused.lat, focused.lon], Math.max(map.getZoom(), 15), { duration: 0.7 });
    const marker = markerRefs.current[focused.id];
    if (marker) {
      // Open popup shortly after the fly animation finishes.
      const t = setTimeout(() => { try { marker.openPopup(); } catch { /* noop */ } }, 750);
      return () => clearTimeout(t);
    }
  }, [focused, map, markerRefs]);
  return null;
};

const DrawHandler = ({ active, onAddPoint, onFinish }) => {
  useMapEvents({
    click: (e) => { if (active) onAddPoint([e.latlng.lat, e.latlng.lng]); },
    dblclick: () => { if (active) onFinish(); },
  });
  return null;
};

const MapView = ({ properties, boardId, canWrite = false, focused = null }) => {
  const { scostBand } = useSettings();
  const scostColor = (v) => scostBand(v).color;
  // Memoise so identity is stable across renders that don't change property
  // coordinates. Prevents AutoZoom from re-firing when the user toggles draw
  // mode, edits a polygon, changes opacity, etc.
  const points = useMemo(
    () => properties.filter((p) => p.lat && p.lon).map((p) => [p.lat, p.lon]),
    [properties]
  );
  const pointsKey = useMemo(() => points.map((p) => `${p[0]},${p[1]}`).join("|"), [points]);
  const center = points[0] || [41.9028, 12.4964];

  // Macroarea shapes state
  const [shapes, setShapes] = useState([]);
  const [shapesVisible, setShapesVisible] = useState(true);
  const [opacity, setOpacity] = useState(30); // percent
  const [drawing, setDrawing] = useState(false);
  const [drawPts, setDrawPts] = useState([]);
  const [savingShapes, setSavingShapes] = useState(false);

  // Inline editor for an existing shape (rename / recolor / edit vertices)
  const [editingIdx, setEditingIdx] = useState(null); // null | number
  const [editDraft, setEditDraft] = useState(null);   // { name, color, coordinates }

  const PRESET_COLORS = [
    "#0A84FF", "#34C759", "#FF9500", "#FF3B30",
    "#AF52DE", "#5AC8FA", "#F5B800", "#00A6B7",
    "#1F7A3A", "#8E8E93",
  ];

  const loadShapes = React.useCallback(async () => {
    if (!boardId) return;
    try { const r = await api.get(`/boards/${boardId}/shapes`); setShapes(r.data || []); }
    catch { /* silent */ }
  }, [boardId]);

  useEffect(() => { loadShapes(); }, [loadShapes]);

  const saveAll = async (list) => {
    if (!boardId) return;
    setSavingShapes(true);
    try {
      await api.put(`/boards/${boardId}/shapes`, { shapes: list });
      await loadShapes(); // reload to get fresh stats
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio forme"); }
    finally { setSavingShapes(false); }
  };

  const finishDraw = async () => {
    if (drawPts.length < 3) { toast.error("Servono almeno 3 punti"); return; }
    const name = window.prompt("Nome della macroarea (es. Prati)", `Macroarea ${shapes.length + 1}`);
    if (!name) { setDrawPts([]); setDrawing(false); return; }
    const newShape = { name: name.trim(), color: "#0A84FF", coordinates: drawPts };
    await saveAll([...shapes, newShape]);
    toast.success(`Macroarea "${name}" salvata`);
    setDrawPts([]);
    setDrawing(false);
  };

  const deleteShape = async (idx) => {
    const next = shapes.filter((_, i) => i !== idx);
    await saveAll(next);
    toast.success("Macroarea eliminata");
  };

  const cancelDraw = () => { setDrawPts([]); setDrawing(false); };

  // --- Edit mode handlers ---------------------------------------------------
  const startEdit = (idx) => {
    if (drawing) return;
    const s = shapes[idx];
    setEditingIdx(idx);
    setEditDraft({
      name: s.name || "",
      color: s.color || "#0A84FF",
      coordinates: (s.coordinates || []).map((c) => [c[0], c[1]]),
    });
  };
  const cancelEdit = () => { setEditingIdx(null); setEditDraft(null); };
  const updateVertex = (vi, latlng) => {
    setEditDraft((prev) => prev ? ({
      ...prev,
      coordinates: prev.coordinates.map((c, i) => i !== vi ? c : [latlng.lat, latlng.lng]),
    }) : prev);
  };
  const removeVertex = (vi) => {
    setEditDraft((prev) => {
      if (!prev) return prev;
      if (prev.coordinates.length <= 3) { toast.error("Un poligono deve avere almeno 3 vertici"); return prev; }
      return { ...prev, coordinates: prev.coordinates.filter((_, i) => i !== vi) };
    });
  };
  const saveEdit = async () => {
    if (editingIdx === null || !editDraft) return;
    const finalName = (editDraft.name || "").trim() || `Macroarea ${editingIdx + 1}`;
    const next = shapes.map((s, i) => i !== editingIdx ? s : {
      name: finalName,
      color: editDraft.color,
      coordinates: editDraft.coordinates,
    });
    setEditingIdx(null);
    setEditDraft(null);
    await saveAll(next);
    toast.success("Macroarea aggiornata");
  };
  // -------------------------------------------------------------------------

  // Refs to individual property markers so FocusHandler can open the right popup.
  const markerRefs = useRef({});

  return (
    <div className="ios-card p-2 relative">
      {/* Toolbar */}
      <div className="absolute top-3 right-3 z-[500] flex items-center gap-2 bg-white/95 backdrop-blur px-3 py-1.5 rounded-full shadow-lg border border-slate-200">
        <button
          onClick={() => setShapesVisible((v) => !v)}
          data-testid="map-toggle-shapes"
          className="flex items-center gap-1 text-[11px] font-semibold text-slate-700 hover:text-[#007AFF]"
          title={shapesVisible ? "Nascondi macroaree" : "Mostra macroaree"}
        >
          {shapesVisible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          <span className="hidden lg:inline">Macroaree</span>
        </button>
        <div className="w-px h-4 bg-slate-200" />
        <label className="flex items-center gap-1.5 text-[10px] text-slate-600" title="Trasparenza forme">
          <span>{opacity}%</span>
          <input
            type="range" min="0" max="100" step="5"
            value={opacity} onChange={(e) => setOpacity(parseInt(e.target.value))}
            data-testid="map-opacity"
            className="w-20 accent-[#007AFF]"
          />
        </label>
        {canWrite && (
          <>
            <div className="w-px h-4 bg-slate-200" />
            {!drawing ? (
              <button
                onClick={() => setDrawing(true)}
                data-testid="map-draw-start"
                className="flex items-center gap-1 text-[11px] font-semibold text-[#007AFF] hover:text-[#0060df]"
                title="Disegna nuova macroarea (click per aggiungere punti, doppio-click per chiudere)"
              >
                <PenLine className="w-3.5 h-3.5" />
                <span className="hidden lg:inline">Disegna</span>
              </button>
            ) : (
              <>
                <span className="text-[10px] font-semibold text-emerald-700">{drawPts.length} punti</span>
                <button onClick={finishDraw} disabled={drawPts.length < 3} className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 disabled:text-slate-400" data-testid="map-draw-finish">Salva</button>
                <button onClick={cancelDraw} className="text-[11px] font-semibold text-rose-600" data-testid="map-draw-cancel"><X className="w-3.5 h-3.5" /></button>
              </>
            )}
          </>
        )}
      </div>

      {/* Draw hint */}
      {drawing && (
        <div className="absolute top-14 right-3 z-[500] bg-slate-900 text-white text-[10px] px-3 py-1.5 rounded-full shadow-lg">
          Click sulla mappa per aggiungere punti. Doppio-click o "Salva" per chiudere.
        </div>
      )}

      {/* Shapes list (deletable + editable) */}
      {shapesVisible && shapes.length > 0 && (
        <div className="absolute bottom-3 left-3 z-[500] bg-white/95 backdrop-blur px-3 py-2 rounded-xl shadow border border-slate-200 w-[280px]">
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Macroaree ({shapes.length})</div>
          <div className="space-y-1 max-h-52 overflow-auto">
            {shapes.map((s, i) => {
              const isEditing = editingIdx === i;
              // Stable key: shape identity is name + colour + first-vertex
              // hash — falls back to index only for brand-new empty rows.
              const firstV = Array.isArray(s.coordinates) && s.coordinates[0];
              const key = `${s.name || "shape"}:${s.color || "0"}:${firstV ? firstV.join(",") : `new-${i}`}`;
              return (
                <div key={key} className={`rounded-lg ${isEditing ? "bg-[#007AFF]/5 p-2" : "px-1 py-0.5"}`}>
                  {!isEditing ? (
                    <div className="flex items-center justify-between gap-2 text-[11px]">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="w-2.5 h-2.5 rounded-sm shrink-0 border border-black/10" style={{ background: s.color }} />
                        <span className="truncate">{s.name}</span>
                      </div>
                      {canWrite && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => startEdit(i)}
                            disabled={savingShapes || drawing}
                            className="text-slate-400 hover:text-[#007AFF] disabled:opacity-40"
                            data-testid={`edit-shape-${i}`}
                            title="Modifica macroarea"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => deleteShape(i)}
                            disabled={savingShapes}
                            className="text-slate-400 hover:text-rose-600 disabled:opacity-40"
                            data-testid={`del-shape-${i}`}
                            title="Elimina macroarea"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editDraft?.name || ""}
                        onChange={(e) => setEditDraft((d) => d ? { ...d, name: e.target.value } : d)}
                        placeholder="Nome macroarea"
                        data-testid={`edit-shape-name-${i}`}
                        className="w-full text-[12px] px-2 py-1 rounded-md bg-white border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#007AFF]/40"
                      />
                      <div className="flex flex-wrap gap-1" data-testid={`edit-shape-palette-${i}`}>
                        {PRESET_COLORS.map((c) => (
                          <button
                            key={c}
                            onClick={() => setEditDraft((d) => d ? { ...d, color: c } : d)}
                            className={`w-4 h-4 rounded-full border transition-transform ${editDraft?.color === c ? "border-slate-900 scale-110 ring-2 ring-slate-300" : "border-white/60"}`}
                            style={{ background: c, boxShadow: "0 1px 2px rgba(0,0,0,0.15)" }}
                            title={c}
                            data-testid={`edit-shape-color-${i}-${c.replace("#", "")}`}
                            aria-label={`Colore ${c}`}
                          />
                        ))}
                        <input
                          type="color"
                          value={editDraft?.color || "#0A84FF"}
                          onChange={(e) => setEditDraft((d) => d ? { ...d, color: e.target.value } : d)}
                          className="w-5 h-5 border-0 bg-transparent cursor-pointer p-0"
                          title="Colore personalizzato"
                          data-testid={`edit-shape-color-picker-${i}`}
                        />
                      </div>
                      <div className="text-[10px] text-slate-500 leading-tight">
                        Trascina i punti azzurri sulla mappa per spostare i vertici.
                        Doppio-click su un vertice per eliminarlo (min 3 punti).
                      </div>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={cancelEdit}
                          className="px-2 py-1 text-[11px] rounded-full text-slate-600 hover:bg-slate-100"
                          data-testid={`edit-shape-cancel-${i}`}
                        >
                          Annulla
                        </button>
                        <button
                          onClick={saveEdit}
                          disabled={savingShapes}
                          className="px-3 py-1 text-[11px] rounded-full bg-[#007AFF] text-white hover:bg-[#0060df] flex items-center gap-1 disabled:opacity-50"
                          data-testid={`edit-shape-save-${i}`}
                        >
                          <Check className="w-3 h-3" /> Salva
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="rounded-2xl overflow-hidden" style={{ height: 460 }}>
        <MapContainer center={center} zoom={12} scrollWheelZoom style={{ height: "100%", width: "100%" }} doubleClickZoom={!drawing}>
          <LayersControl position="topleft">
            <LayersControl.BaseLayer checked name="Mappa">
              <TileLayer
                attribution='&copy; OpenStreetMap'
                url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                maxZoom={19}
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Satellite">
              <TileLayer
                attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                maxZoom={19}
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Ibrida (Satellite + Etichette)">
              <LayerGroup>
                <TileLayer
                  attribution='Tiles &copy; Esri'
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  maxZoom={19}
                />
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                  maxZoom={19}
                />
              </LayerGroup>
            </LayersControl.BaseLayer>
          </LayersControl>

          {/* Macroarea shapes layer — between base tiles and markers */}
          {shapesVisible && (
            <LayerGroup>
              {shapes.map((s, i) => {
                // When editing this shape, draw the in-progress draft instead.
                const isEditing = editingIdx === i;
                const displayed = isEditing && editDraft ? editDraft : s;
                return (
                  <Polygon
                    key={i}
                    positions={displayed.coordinates}
                    pathOptions={{
                      color: displayed.color, weight: isEditing ? 3 : 2,
                      dashArray: isEditing ? "6 4" : undefined,
                      fillColor: displayed.color, fillOpacity: opacity / 100,
                    }}
                  >
                    {!isEditing && (
                      <Popup>
                        <div className="text-[12px]" style={{ minWidth: "170px" }}>
                          <div className="font-semibold text-slate-900">{s.name}</div>
                          <div className="text-slate-500 text-[10px] uppercase tracking-wider">Macroarea</div>
                          {s.stats && s.stats.count > 0 ? (
                            <div className="mt-2 space-y-0.5 font-mono-num">
                              <div><b>{s.stats.count}</b> immobili nel poligono</div>
                              <div className="text-slate-700">
                                Media €/m²: <b style={{ color: s.color }}>{fmtEuro(s.stats.avg_eur_mq)}</b>
                              </div>
                              <div className="text-slate-500">Media prezzo: {fmtEuro(s.stats.avg_prezzo)}</div>
                              <div className="text-slate-500 text-[10px]">Media MQ: {Math.round(s.stats.avg_mq)} m²</div>
                              <div className="text-slate-400 text-[10px] mt-1">
                                Range €/m²: {fmtEuro(s.stats.min_eur_mq)} – {fmtEuro(s.stats.max_eur_mq)}
                              </div>
                            </div>
                          ) : (
                            <div className="mt-2 text-slate-400 text-[11px] italic">Nessun immobile geolocalizzato dentro il poligono</div>
                          )}
                        </div>
                      </Popup>
                    )}
                  </Polygon>
                );
              })}
              {/* Draggable vertex markers when editing */}
              {editingIdx !== null && editDraft && editDraft.coordinates.map((pt, vi) => (
                <Marker
                  key={`vx-${vi}`}
                  position={pt}
                  draggable
                  eventHandlers={{
                    drag: (e) => updateVertex(vi, e.target.getLatLng()),
                    dblclick: () => removeVertex(vi),
                  }}
                  icon={L.divIcon({
                    className: "vertex-dot",
                    html: `<div style="width:12px;height:12px;border-radius:50%;background:#0A84FF;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.4);cursor:grab;"></div>`,
                    iconSize: [12, 12],
                    iconAnchor: [6, 6],
                  })}
                />
              ))}
              {/* Draw-in-progress preview */}
              {drawing && drawPts.length >= 2 && (
                <Polygon positions={drawPts} pathOptions={{ color: "#007AFF", dashArray: "6 6", weight: 2, fillOpacity: 0.10 }} />
              )}
              {drawing && drawPts.map((p, i) => (
                <CircleMarker key={i} center={p} radius={4} pathOptions={{ color: "#007AFF", fillColor: "#fff", fillOpacity: 1, weight: 2 }} />
              ))}
            </LayerGroup>
          )}

          <DrawHandler active={drawing} onAddPoint={(pt) => setDrawPts((prev) => [...prev, pt])} onFinish={finishDraw} />

          {/* Property markers (on top of shapes) */}
          {properties.filter((p) => p.lat && p.lon).map((p) => {
            const b = scostBand(p.scostamento);
            return (
              <Marker
                key={p.id}
                position={[p.lat, p.lon]}
                icon={makeDot(b.color)}
                ref={(ref) => { if (ref) markerRefs.current[p.id] = ref; else delete markerRefs.current[p.id]; }}
              >
                <Popup>
                  <div className="text-[13px] leading-tight airbnb-popup" style={{ minWidth: "200px" }}>
                    <div className="font-semibold text-slate-900 flex items-start gap-1.5">
                      <span className="text-slate-500 mt-0.5 shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/></svg>
                      </span>
                      <span>{p.indirizzo}</span>
                    </div>
                    <div className="text-slate-500 text-[11px] uppercase tracking-wider mt-0.5 flex items-center gap-1 pl-4">
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>
                      {p.macro_area}
                    </div>
                    <div className="mt-2 space-y-1 font-mono-num">
                      <div className="flex items-center gap-1.5 text-slate-900">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-slate-500 shrink-0"><path d="M4 10h12"/><path d="M4 14h9"/><path d="M19 6l0 12"/><path d="M14 3l0 3"/><path d="M14 18l0 3"/></svg>
                        <b>{fmtEuro(p.prezzo)}</b>
                      </div>
                      <div className="flex items-center gap-1.5 text-slate-700">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-slate-500 shrink-0"><path d="m21.3 15.3-6.6-6.6a2 2 0 0 0-2.8 0l-6.6 6.6a2 2 0 0 0 0 2.8l1.6 1.6"/><path d="m8 8 3 3"/><path d="m12 4 3 3"/><path d="m16 12 3 3"/></svg>
                        {p.mq} m² · {fmtEuro(p.eur_mq)} €/m²
                      </div>
                      {p.piano && (
                        <div className="flex items-center gap-1.5 text-slate-600 text-[12px]">
                          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-slate-500 shrink-0"><rect width="16" height="20" x="4" y="2" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/></svg>
                          Piano {p.piano}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-[11px] text-slate-600">
                        <span className="inline-flex items-center gap-1">
                          <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={p.ascensore ? "text-emerald-600" : "text-slate-300"}><path d="m7 15 5 5 5-5"/><path d="m7 9 5-5 5 5"/></svg>
                          {p.ascensore ? "Ascensore" : "Senza ascensore"}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={(p.da_ristrutturare === "SI" || p.da_ristrutturare === true) ? "text-amber-600" : "text-slate-300"}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
                          {p.da_ristrutturare === "SI" || p.da_ristrutturare === true ? "Da ristrutturare" : p.da_ristrutturare === "NO" || p.da_ristrutturare === false ? "Non da ristrutturare" : "Ristrutt.: NI"}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center gap-1.5" style={{ color: b.color }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><polyline points="22 17 13.5 8.5 8.5 13.5 2 7"/><polyline points="16 17 22 17 22 11"/></svg>
                        Scostam.: <b>{fmtPct(p.scostamento)}</b>
                      </div>
                    </div>
                    {p.note && (
                      <div className="mt-2 text-[11px] text-slate-700 bg-amber-50 border border-amber-200 rounded-md p-1.5 whitespace-pre-wrap flex items-start gap-1.5">
                        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600 shrink-0 mt-0.5"><path d="M15.5 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8.5z"/><path d="M15 3v6h6"/></svg>
                        <span>{p.note.length > 160 ? p.note.slice(0, 160) + "…" : p.note}</span>
                      </div>
                    )}
                    {p.link && <PopupLinkWithPreview href={p.link} preview={p.preview_image} />}
                  </div>
                </Popup>
              </Marker>
            );
          })}

          <AutoZoom pointsKey={pointsKey} points={points} />
          <FocusHandler focused={focused} markerRefs={markerRefs} />
        </MapContainer>
      </div>
    </div>
  );
};

export default MapView;
